import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InfoGroupComponent, TagComponent } from '@tecalisdev/infinity';
import { MasterService } from '../../../../shared/services/master.service';
import {
	CONNECTION_TYPE,
	IOperationBormeDetail,
	IOperationDetailResponse,
	IOperationTfaMessagingDetailData,
	isTfaOperationDetail,
	OPERATION_TYPE,
} from '../../../models/operations.models';
import { IOperationPolDetail } from '../../../../processes/models/processes.models';
import { resolveTfaChannel, resolveTfaChannelLabelKey } from '../../../utils/tfa-display.util';
import { notAvailableLabel } from '../../../../core/utils/utils';

@Component({
	selector: 'app-operation-detail-item-extra-info',
	imports: [TranslatePipe, InfoGroupComponent, TagComponent],
	templateUrl: './operation-detail-item-extra-info.component.html',
})
export class OperationDetailItemExtraInfoComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	public masterService = inject(MasterService);
	private trans = inject(TranslateService);

	public OPERATION_TYPE = OPERATION_TYPE;
	public captureModeTranslationKey = 'OPERATIONS.DETAIL.CAPTURE_MODE.UNKNOWN';

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		const operationType = this.detail?.operation_type_name as OPERATION_TYPE | undefined;

		if (operationType === OPERATION_TYPE.POL) {
			this.captureModeTranslationKey = this.polCaptureModeTranslationKey();
		}
	}

	public polCaptureModeTranslationKey(): string {
		if (!this.detail || this.detail.operation_type_name !== OPERATION_TYPE.POL) {
			return 'OPERATIONS.DETAIL.CAPTURE_MODE.UNKNOWN';
		}
		const polData = this.detail.data as IOperationPolDetail | undefined;
		const mode = polData?.mode?.toLowerCase() ?? '';
		if (mode === 'auto') {
			return 'OPERATIONS.DETAIL.CAPTURE_MODE.AUTO';
		}
		if (mode === 'manual') {
			return 'OPERATIONS.DETAIL.CAPTURE_MODE.MANUAL';
		}
		return 'OPERATIONS.DETAIL.CAPTURE_MODE.UNKNOWN';
	}

	public isFinancialEvaluationOperation(): boolean {
		const operationType = this.detail?.operation_type_name as OPERATION_TYPE | undefined;
		return (
			operationType === OPERATION_TYPE.CONNECTIONS ||
			operationType === OPERATION_TYPE.FINANCIAL_EVALUATION ||
			operationType === OPERATION_TYPE.EVALUATION
		);
	}

	public isBormeOperation(): boolean {
		return this.detail?.operation_type_name === OPERATION_TYPE.BORME;
	}

	public isTfaOperation(): boolean {
		return isTfaOperationDetail(this.detail);
	}

	public tfaChannelDisplayLabel(): string {
		if (!this.isTfaOperation()) return notAvailableLabel(this.trans);
		const data = this.detail?.data as IOperationTfaMessagingDetailData | undefined;
		const channel = resolveTfaChannel(data ?? {});
		const key = resolveTfaChannelLabelKey(channel);
		if (key) {
			return this.trans.instant(key);
		}
		return channel || notAvailableLabel(this.trans);
	}

	public bormeTaxIdLabel(): string {
		if (!this.isBormeOperation()) return notAvailableLabel(this.trans);
		const data = this.detail?.data as IOperationBormeDetail | undefined;
		return data?.tax_id?.trim() || notAvailableLabel(this.trans);
	}

	public bormeCompanyNameLabel(): string {
		if (!this.isBormeOperation()) return notAvailableLabel(this.trans);
		const data = this.detail?.data as IOperationBormeDetail | undefined;
		return data?.company_name?.trim() || notAvailableLabel(this.trans);
	}

	public financialEvaluationTypeLabel(): string {
		if (!this.isFinancialEvaluationOperation()) return notAvailableLabel(this.trans);
		const type =
			(this.detail?.financial_evaluation_type ?? (this.detail?.data as Record<string, unknown> | undefined)?.['type']) ??
			'';
		if (!type) return notAvailableLabel(this.trans);
		const normalizedType = this.normalizeConnectionType(String(type));
		const key = 'FINANCIAL_EVALUATION.' + normalizedType;
		return this.masterService.hasTranslation(key) ? this.trans.instant(key) : normalizedType;
	}

	private normalizeConnectionType(rawType: string): string {
		const type = rawType.trim().toUpperCase();
		const keyMatch = (CONNECTION_TYPE as Record<string, string>)[type];
		if (keyMatch) return keyMatch;
		if (type === 'WORK_LIFE') return CONNECTION_TYPE.WORK_LIFE;
		return type;
	}

	/** Tipos solicitados (DOCS.*) o tipo de documento maestro cuando no hay lista `types`. */
	public documentsTypesLabel(): string {
		if (!this.detail || this.detail.operation_type_name !== OPERATION_TYPE.DOCUMENTS) {
			return notAvailableLabel(this.trans);
		}
		const types = this.detail.types as Array<{ type?: string }> | undefined;
		if (types?.length) {
			const labels = types
				.map((t) => {
					const code = t?.type;
					if (!code) return '';
					const key = 'DOCS.' + code.toUpperCase();
					return this.masterService.hasTranslation(key) ? this.trans.instant(key) : code;
				})
				.filter((s) => s.length > 0);
			if (labels.length) return labels.join(', ');
		}
		const dt = this.detail.doc_type;
		if (dt) {
			return this.masterService.getDocumentTypeLabel(dt);
		}
		return notAvailableLabel(this.trans);
	}
}
